import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type {
  TLngLat,
  TValuesCurrentBlocks,
  TValuesCurrentHeatmapPoints,
  TValuesCurrentPoints,
  TValuesCurrentProperties,
  TValuesCurrentRegions,
} from 'App/Map/types'
import { isEmpty, isNil } from 'lodash'
import type { TRGBAColorWith1AtTheEnd } from 'settings/colors'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TBlockId, TPropertyId, TRegionId } from 'stores/fieldAssetStore/helperTypes'
import { getPrimaryValueGroup } from 'stores/selectedValueGroupsStore/getPrimaryValueGroup'
import {
  degreeDaysRecord,
  larvalTrendRecord,
  phenologyRecord,
  trapCatchesRecord,
} from 'stores/selectedValueGroupsStore/insectValueGroupRecords'
import type { TValueGroup } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { doesBlockHaveValueType } from 'utils/doesFieldAssetHaveValueType'
import { lngLatStringToLngLatObject } from 'utils/lngLatStringToLngLatObject'
import type { TUnitConverterFunction } from 'utils/unitConverter/unitConverter'
import { isRegionDataEnabled } from 'utils/useIsRegionDataEnabled'
import type { TValueType } from '../../../caches/BlockScdsValuesCache/BlockScdsValuesCache'
import type {
  TCurrentValuesMapCacheKeys,
  TCurrentValuesMapProcessedCaches,
  TGetCacheUpdatesFromResponseReturn,
} from '../_types'
import { getHeatmapCSS } from './getHeatmapCSS'
import { getPropertyIdsForAllPropertiesInVisibleRegions } from './getPropertyIdsForAllPropertiesInVisibleRegions'
import { setHeatmapExtremesFromArrayOfValues } from './setHeatmapExtremesFromArrayOfValues'

export const getIsDeviceReported = (source: string | null) => {
  if (source === '1') return true

  return false
}

const includeDataSource: Record<TValueGroup, boolean> = {
  air_temperature: true,
  almond_bloom: false,
  almond_hull_split: false,
  alternaria: false,
  atmospheric_pressure: false,
  bee_hours: false,
  cherry_powdery_mildew: false,
  chill: false,
  conditions: false,
  ...degreeDaysRecord(() => false),
  dew_point: true,
  equipment_status: false,
  fire_blight: false,
  evapotranspiration: false,
  fruit_growth: false,
  grape_powdery_mildew: false,
  plant_stress: false,
  humidity: true,
  irrigation_activity_pressure: false,
  irrigation_activity_zones: false,
  irrigation_activity_flow_meters: false,
  ...larvalTrendRecord(() => false),
  leaf_wetness: false,
  ndvi: false,
  precipitation: false,
  soil: false,
  spray_conditions: false,
  ...trapCatchesRecord(() => false),
  ...phenologyRecord(() => false),
  wet_bulb: true,
  wind: false,
  wind_machine: false,
}

const isWeatherStationValue: Record<TValueGroup, boolean> = {
  air_temperature: true,
  alternaria: false,
  almond_bloom: true,
  almond_hull_split: true,
  atmospheric_pressure: true,
  bee_hours: true,
  cherry_powdery_mildew: true,
  chill: true,
  conditions: true,
  ...degreeDaysRecord(() => true),
  dew_point: true,
  equipment_status: false,
  evapotranspiration: false,
  fire_blight: true,
  fruit_growth: false,
  grape_powdery_mildew: true,
  plant_stress: false,
  humidity: true,
  irrigation_activity_pressure: false,
  irrigation_activity_zones: false,
  irrigation_activity_flow_meters: false,
  ...larvalTrendRecord(() => false),
  leaf_wetness: true,
  ndvi: false,
  precipitation: true,
  soil: false,
  spray_conditions: false,
  ...trapCatchesRecord(() => false),
  ...phenologyRecord(() => false),
  wet_bulb: true,
  wind: true,
  wind_machine: true,
}

export type TGenerateUsualStyleGetCacheUpdatesFromResponseArgs = {
  cacheKeys: TCurrentValuesMapCacheKeys
  processedCaches: TCurrentValuesMapProcessedCaches
  response: routes.ValuesCurrent.Response
  block?: {
    decimalPlaces?: 0 | 1
    valueType: TValuesCurrentBlocks
    unitConverterFunction: TUnitConverterFunction
    valueKey: string
  }
  inBlockPoint?: {
    decimalPlaces?: 0 | 1
    valueType: TValuesCurrentPoints | TValuesCurrentHeatmapPoints
    unitConverterFunction: TUnitConverterFunction
  }
  trapPoint?: {
    decimalPlaces?: 0 | 1
    valueType: TValuesCurrentPoints
    unitConverterFunction: TUnitConverterFunction
  }
  outOfBlockPoint?: {
    decimalPlaces?: 0 | 1
    valueType: TValuesCurrentPoints
    unitConverterFunction: TUnitConverterFunction
  }
  property?: {
    decimalPlaces?: 0 | 1
    valueType: TValuesCurrentProperties
    unitConverterFunction: TUnitConverterFunction
  }
  region?: {
    decimalPlaces?: 0 | 1
    valueType: TValuesCurrentRegions
    unitConverterFunction: TUnitConverterFunction
  }
  heatmapColoring?: TRGBAColorWith1AtTheEnd[]
  heatmapExtremesForAllPropertiesInVisibleRegions: boolean
}

/**
 * we can manually populate the SCDs for each block,
 * since we don't include SCDs in the user-app-startup
 */
export const populateSCDsForBlocks = (
  response: routes.ValuesCurrent.Response,
  processedCaches: TCurrentValuesMapProcessedCaches,
) => {
  const blocksToSCDs: Record<TBlockId, TLngLat[]> = {}

  Object.entries(response?.heatmap_points ?? {}).forEach(([lngLat, { blockIds }]) => {
    blockIds?.forEach((blockId) => {
      blocksToSCDs[blockId] = blocksToSCDs[blockId] ?? []

      blocksToSCDs[blockId].push(lngLat as TLngLat)
    })
  })

  processedCaches.blockScdsValues.itemsWithinView.forEach((block) => {
    block.meta.scds = !blocksToSCDs[Number(block.id)]
      ? []
      : blocksToSCDs[Number(block.id)].map((lngLat) => ({
          lngLat,
          googleLatLng: new google.maps.LatLng(lngLatStringToLngLatObject(lngLat)),
        }))
  })

  return processedCaches
}

export const generateUsualStyleGetCacheUpdatesFromResponse = ({
  cacheKeys,
  processedCaches: processedCachesArg,
  response,
  block: blockArg,
  inBlockPoint,
  outOfBlockPoint,
  trapPoint,
  property: propertyArg,
  heatmapColoring,
  heatmapExtremesForAllPropertiesInVisibleRegions,
  region: regionArg,
}: TGenerateUsualStyleGetCacheUpdatesFromResponseArgs): TGetCacheUpdatesFromResponseReturn => {
  const processedCaches = populateSCDsForBlocks(response, processedCachesArg)
  const returner: TGetCacheUpdatesFromResponseReturn = {}
  const allValues: (number | null)[] = []
  const { showDataSource } = smallStore.getState()
  const { properties } = fieldAssetStore.getState()
  const primaryValueGroup = getPrimaryValueGroup()
  const isSourcePrimaryGroupSelected = !!includeDataSource[primaryValueGroup as TValueGroup]

  if (blockArg && !!processedCaches.blockValues.itemsWithinView.length) {
    const itemIdsWithinView: string[] = []

    const itemsWithinViewThatNowHaveValues = processedCaches.blockValues.itemsWithinView.flatMap((block) => {
      // @ts-expect-error
      const { [blockArg.valueKey]: value } = response?.blocks?.[block.id]?.[blockArg.valueType] ?? {}

      if (primaryValueGroup === 'almond_bloom' && isNil(value)) {
        const blockCouldHaveValue = doesBlockHaveValueType({
          propertyId: block.meta.propertyId,
          blockId: Number(block.id),
          currentValueToCheck: blockArg.valueType,
        })

        if (!blockCouldHaveValue) {
          return []
        }
      } else if (isNil(value)) {
        return []
      }

      itemIdsWithinView.push(block.id)

      allValues.push(value)

      return {
        id: String(block.meta.blockId),
        value: {
          [blockArg.valueType]: {
            children: blockArg
              .unitConverterFunction(value, { decimalPlaces: blockArg.decimalPlaces ?? 0 })
              .valueAsString(),
            getContainerCSS: () => getHeatmapCSS({ value }),
          },
        },
      }
    })

    returner.blocks = {
      itemsWithinViewThatNowHaveValues,
      cacheKey: cacheKeys.blockValuesCacheKey,
      itemIdsWithinView,
    }
  }

  if (inBlockPoint && !!processedCaches.blockScdsValues.itemsWithinView.length) {
    const itemIdsWithinView: string[] = []

    const itemsWithinViewThatNowHaveValues = processedCaches.blockScdsValues.itemsWithinView.flatMap(
      (block) => {
        return {
          id: block.meta.blockId,
          value: block.meta.scds.reduce((acc: TValueType, { googleLatLng, lngLat }) => {
            // @ts-expect-error
            const { value, source } = response?.heatmap_points?.[lngLat]?.[inBlockPoint.valueType] ?? {}
            const isDeviceReported = getIsDeviceReported(source)

            itemIdsWithinView.push(block.id)

            allValues.push(value)

            acc[googleLatLng.toUrlValue()] = {
              [inBlockPoint.valueType]: {
                children: inBlockPoint
                  .unitConverterFunction(value, { decimalPlaces: inBlockPoint.decimalPlaces ?? 0 })
                  .valueAsString(),
                getContainerCSS: () => getHeatmapCSS({ value }),
                shouldAddDataSourceOverlay:
                  !isNil(value) && !isDeviceReported && isSourcePrimaryGroupSelected && showDataSource,
              },
            }

            return acc
          }, {}),
        }
      },
    )

    returner.heatmapPoints = {
      itemsWithinViewThatNowHaveValues,
      cacheKey: cacheKeys.scdCacheKey,
      itemIdsWithinView: [...new Set(itemIdsWithinView)],
    }
  }

  if ((inBlockPoint || outOfBlockPoint) && !!processedCaches.stationValues.itemsWithinView.length) {
    const itemIdsWithinView: string[] = []

    const itemsWithinViewThatNowHaveValues = processedCaches.stationValues.itemsWithinView.flatMap(
      (station) => {
        let keyToUse = inBlockPoint?.valueType
        let decimalPlacesToUse = inBlockPoint?.decimalPlaces
        let unitConverterToUse = inBlockPoint?.unitConverterFunction

        if (station.meta.isOutOfBlock && outOfBlockPoint) {
          keyToUse = outOfBlockPoint.valueType

          decimalPlacesToUse = outOfBlockPoint.decimalPlaces ?? decimalPlacesToUse

          unitConverterToUse = outOfBlockPoint.unitConverterFunction
        }

        if (!keyToUse || !unitConverterToUse) return []

        // @ts-expect-error TODO: coordinate types a bit better
        const value = response?.points?.[station.meta.lngLat]?.[keyToUse]?.value as number | null
        // @ts-expect-error
        const source = response?.points?.[station.meta.lngLat]?.[keyToUse]?.source as string | null
        const isDeviceReported = getIsDeviceReported(source)

        const isVirtual =
          properties?.[station.meta.propertyId]?.points?.[station.meta.lngLat].isVirtual ?? false

        itemIdsWithinView.push(station.id)

        allValues.push(value)

        const children = unitConverterToUse(value, { decimalPlaces: decimalPlacesToUse ?? 0 }).valueAsString()

        return {
          id: String(station.id),
          value: {
            [keyToUse]: {
              children,
              getContainerCSS: () => getHeatmapCSS({ value }),
              getSize: () => {
                let size = outOfBlockPoint ? 36 : 40

                if (children.length > 3) size = size + (children.length - 3) * 6

                return size
              },
              shouldAddDataSourceOverlay:
                !isNil(value) &&
                !isVirtual &&
                !isDeviceReported &&
                isSourcePrimaryGroupSelected &&
                showDataSource,
              isWeatherStation: primaryValueGroup ? isWeatherStationValue[primaryValueGroup] : false,
            },
          },
        }
      },
    )

    returner.stations = {
      itemsWithinViewThatNowHaveValues,
      cacheKey: cacheKeys.stationCacheKey,
      itemIdsWithinView,
    }
  }

  if (trapPoint && !!processedCaches.trapsValues.itemsWithinView.length) {
    const itemIdsWithinView: string[] = []

    const itemsWithinViewThatNowHaveValues = processedCaches.trapsValues.itemsWithinView.flatMap((trap) => {
      const value = response?.points?.[trap.meta.lngLat]?.[trapPoint.valueType]?.value as number | null

      itemIdsWithinView.push(trap.id)

      allValues.push(value)

      const children = trapPoint
        .unitConverterFunction(value, { decimalPlaces: trapPoint.decimalPlaces ?? 0 })
        .valueAsString()

      return {
        id: String(trap.id),
        value: {
          [trapPoint.valueType]: {
            children,
            getContainerCSS: () => getHeatmapCSS({ value }),
          },
        },
      }
    })

    returner.traps = {
      itemsWithinViewThatNowHaveValues,
      cacheKey: cacheKeys.trapsCacheKey,
      itemIdsWithinView,
    }
  }

  const willShowStations = !!returner.stations?.itemIdsWithinView.length
  const willShowBlocks = !!returner.blocks?.itemIdsWithinView.length
  const willShowHeatmapPoints = !!returner.heatmapPoints?.itemIdsWithinView.length
  const willShowTraps = !!returner.traps?.itemIdsWithinView.length
  const propertyIdsToValues: Record<TPropertyId, number | null> = {}
  const propertyIds = processedCaches.propertyValues.itemsWithinView.map((s) => Number(s.id))
  const regionIdsToValues: Record<TRegionId, number | null> = {}
  const regionIds = processedCaches.regionValues?.itemIdsWithinView || []

  if (regionArg && regionArg.valueType && isRegionDataEnabled()) {
    regionIds.forEach((regionId) => {
      const valueTypeForRegion = response?.regions?.[regionId]?.[regionArg.valueType]

      let valueForRegion = null

      if (valueTypeForRegion && 'median_value' in valueTypeForRegion) {
        valueForRegion = valueTypeForRegion.median_value
      } else if (
        valueTypeForRegion &&
        'value' in valueTypeForRegion &&
        typeof valueTypeForRegion.value === 'number'
      ) {
        valueForRegion = valueTypeForRegion.value
      }

      regionIdsToValues[regionId] = valueForRegion
    })
  }

  if (propertyArg && propertyArg.valueType) {
    propertyIds.forEach((propertyId) => {
      const valueTypeForProperty = response?.properties?.[propertyId]?.[propertyArg.valueType]

      let valueForProperty = null

      if (valueTypeForProperty && 'value' in valueTypeForProperty) {
        valueForProperty = valueTypeForProperty.value
      }

      if (valueTypeForProperty && 'median_value' in valueTypeForProperty) {
        valueForProperty = valueTypeForProperty.median_value
      }

      if (typeof valueForProperty !== 'string' && !Array.isArray(valueForProperty)) {
        // @ts-expect-error
        propertyIdsToValues[propertyId] = valueForProperty
      }
    })
  }

  // TODO: not sure about this logic
  const showValuesForRegionRatherThanName = regionArg && !isEmpty(regionIdsToValues)

  const regionItemsWithinViewThatNowHaveValues = processedCaches.regionValues?.itemsWithinView.flatMap(
    (region) => {
      if (!showValuesForRegionRatherThanName) {
        return {
          id: region.id,
          value: {
            [String(regionArg?.valueType)]: {
              children: null,
              onHover: false,
              getContainerCSS: () => ({}),
              baseZIndex: undefined,
            },
          },
        }
      }

      const value = regionIdsToValues[region.id]

      allValues.push(value)

      return {
        id: region.id,
        value: {
          [String(regionArg?.valueType)]: {
            children: regionArg
              .unitConverterFunction(value, { decimalPlaces: regionArg.decimalPlaces ?? 0 })
              .valueAsString(),
            onHover: true,
            getContainerCSS: () => {
              if (isNil(value)) {
                return { display: 'none' }
              }

              return getHeatmapCSS({ value })
            },
            baseZIndex: 1000,
          },
        },
      }
    },
  )

  const showValuesForPropertyRatherThanName =
    propertyArg &&
    !isEmpty(propertyIdsToValues) &&
    !willShowStations &&
    !willShowBlocks &&
    !willShowHeatmapPoints &&
    !willShowTraps

  const propertyItemsWithinViewThatNowHaveValues = processedCaches.propertyValues.itemsWithinView.flatMap(
    (property) => {
      if (!showValuesForPropertyRatherThanName) {
        return {
          id: String(property.id),
          value: {
            [String(propertyArg?.valueType)]: {
              children: property.meta.propertyName,
              onHover: false,
              getContainerCSS: () => ({ backgroundColor: colors.midnight, color: 'white' }),
              baseZIndex: undefined,
            },
          },
        }
      }

      const value = propertyIdsToValues[Number(property.id)]

      allValues.push(value)

      return {
        id: String(property.id),
        value: {
          [String(propertyArg?.valueType)]: {
            children: propertyArg
              .unitConverterFunction(value, { decimalPlaces: propertyArg.decimalPlaces ?? 0 })
              .valueAsString(),
            onHover: true,
            getContainerCSS: () => getHeatmapCSS({ value }),
            baseZIndex: isNil(value) ? -10 : undefined,
          },
        },
      }
    },
  )

  if (primaryValueGroup && regionArg) {
    returner.regions = {
      itemsWithinViewThatNowHaveValues: regionItemsWithinViewThatNowHaveValues ?? [],
      cacheKey: cacheKeys.regionValuesCacheKey,
      itemIdsWithinView: processedCaches.regionValues?.itemIdsWithinView ?? [],
    }
  }

  returner.properties = {
    itemsWithinViewThatNowHaveValues: propertyItemsWithinViewThatNowHaveValues,
    cacheKey: cacheKeys.propertyCacheKey,
    itemIdsWithinView: processedCaches.propertyValues.itemIdsWithinView,
  }

  let valuesToUseForExtremes = allValues

  if (heatmapExtremesForAllPropertiesInVisibleRegions && propertyArg?.valueType) {
    const visiblePropertyIds = processedCaches.propertyValues.itemsWithinView.map((s) => Number(s.id))
    const allPropertyIdsInVisibleRegions = getPropertyIdsForAllPropertiesInVisibleRegions(visiblePropertyIds)

    if (allPropertyIdsInVisibleRegions.length > 1) {
      const valuesForAllPropertiesInVisibleRegion: number[] = []

      allPropertyIdsInVisibleRegions.forEach((propertyId) => {
        const valueTypeResponseForProperty = response?.properties?.[propertyId]?.[propertyArg?.valueType]

        if (!isNil(valueTypeResponseForProperty)) {
          const foundValueForProperty =
            'median_value' in valueTypeResponseForProperty
              ? valueTypeResponseForProperty.median_value
              : valueTypeResponseForProperty.value

          if (!isNil(foundValueForProperty) && typeof foundValueForProperty === 'number') {
            valuesForAllPropertiesInVisibleRegion.push(foundValueForProperty)
          }
        }
      })

      if (valuesForAllPropertiesInVisibleRegion.length > 1) {
        valuesToUseForExtremes = valuesForAllPropertiesInVisibleRegion
      }
    }
  }

  setHeatmapExtremesFromArrayOfValues({
    values: valuesToUseForExtremes,
    heatmapColoring,
    isStale: processedCaches.isStale,
  })

  return returner
}
